import { array, boolean, number, object, string } from 'yup';

// Base schema for common fields
const baseSchema = {
  // Core configuration fields
  active: boolean().required('Active status is required'),

  // Fee configuration
  fee_account: string().optional(), // Hidden field, not required from user
  fee: string()
    .required('Fee is required')
    .test('is-valid-percentage', 'Must be a valid percentage (0.00 - 100.00)', value => {
      if (!value) return false;
      const num = parseFloat(value);
      return !isNaN(num) && num >= 0 && num <= 100;
    }),

  // Decimals - must be >= 0
  decimals: number()
    .required('Decimals is required')
    .min(0, 'Decimals must be at least 0')
    .integer('Must be an integer'),

  // Mint address (Solana token address)
  mint: string()
    .required('Token mint address is required')
    .trim() // Remove leading/trailing whitespace
    .test('is-valid-solana-address', 'Must be a valid Solana address', value => {
      if (!value) return false;
      // Solana addresses are base58 encoded strings, typically 32-44 characters
      // This is a basic check - a more thorough validation would decode and verify
      const solanaAddressRegex = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;
      return solanaAddressRegex.test(value);
    }),

  // Slippage configuration - percentage (0.00 - 100.00)
  slippage: string()
    .required('Slippage is required')
    .test('is-valid-percentage', 'Must be a valid percentage (0.00 - 100.00)', value => {
      if (!value) return false;
      const num = parseFloat(value);
      return !isNaN(num) && num >= 0 && num <= 100;
    }),

  // Target price - must be > 0
  target_price: string()
    .required('Target price is required')
    .test('is-valid-positive-number', 'Must be a number greater than 0', value => {
      if (!value) return false;
      const num = parseFloat(value);
      return !isNaN(num) && num > 0;
    }),

  // Target volume in Solana - must be > 0
  target_volume_in_solana: string()
    .required('Target volume in Solana is required')
    .test('is-valid-positive-number', 'Must be a number greater than 0', value => {
      if (!value) return false;
      const num = parseFloat(value);
      return !isNaN(num) && num > 0;
    }),

  // Volume percentage - (0.00 - 100.00)
  volume_percentage: string()
    .required('Volume percentage is required')
    .test('is-valid-percentage', 'Must be a valid percentage (0.00 - 100.00)', value => {
      if (!value) return false;
      const num = parseFloat(value);
      return !isNaN(num) && num >= 0 && num <= 100;
    }),
};

// Schema for create mode (requires wallet selection)
export const createModeSchema = object({
  ...baseSchema,
  // Wallet IDs - required for create mode (at least one wallet must be selected)
  wallet_ids: array()
    .of(number().required('Wallet ID is required'))
    .required('At least one wallet must be selected')
    .min(1, 'At least one wallet must be selected'),
});

// Schema for edit mode (wallet changes are optional)
export const editModeSchema = object({
  ...baseSchema,
  // Wallet IDs - optional for edit mode (existing wallets can be modified separately)
  wallet_ids: array().of(number().required('Wallet ID is required')).optional(),
});

// Main validation schema (for backward compatibility)
export const SolanaCounterTradeBotValidationSchema = createModeSchema;
